import { useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Loader2, Building2, Globe, Mail, Phone, MapPin, Hash } from 'lucide-react';

const PRIMARY_FIELDS = [
  { key: 'company_name', label: 'Company', testId: 'business-lead-detail-company-name' },
  { key: 'contact_name', label: 'Contact', testId: 'business-lead-detail-contact-name' },
  { key: 'email', label: 'Email', testId: 'business-lead-detail-email' },
  { key: 'phone', label: 'Phone', testId: 'business-lead-detail-phone' },
  { key: 'website', label: 'Website', testId: 'business-lead-detail-website' },
  { key: 'business_type', label: 'Business Type', testId: 'business-lead-detail-business-type' },
  { key: 'industry', label: 'Industry', testId: 'business-lead-detail-industry' },
  { key: 'sic_code', label: 'SIC', testId: 'business-lead-detail-sic-code' },
  { key: 'naics_code', label: 'NAICS', testId: 'business-lead-detail-naics-code' },
  { key: 'employee_count', label: 'Employees', testId: 'business-lead-detail-employee-count' },
  { key: 'annual_revenue', label: 'Annual Revenue', testId: 'business-lead-detail-annual-revenue' },
  { key: 'state', label: 'State', testId: 'business-lead-detail-state' },
  { key: 'city', label: 'City', testId: 'business-lead-detail-city' },
  { key: 'zip_code', label: 'Zip', testId: 'business-lead-detail-zip-code' },
  { key: 'source_provider', label: 'Source', testId: 'business-lead-detail-source-provider' },
];

const HIDDEN_DYNAMIC_KEYS = new Set([
  'id',
  'external_business_id',
  'lead_score',
  'lead_score_breakdown',
  'lead_score_updated_at',
  'lead_score_updated_by',
  'custom_fields',
  ...PRIMARY_FIELDS.map((field) => field.key),
]);

const formatValue = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const BusinessLeadDetailModal = ({ open, onOpenChange, lead, loading, sourceLabel = 'business-lead' }) => {
  const dynamicEntries = useMemo(
    () => Object.entries(lead || {}).filter(([key]) => !HIDDEN_DYNAMIC_KEYS.has(key)),
    [lead]
  );

  const customEntries = useMemo(
    () => Object.entries(lead?.custom_fields || {}),
    [lead]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="business-lead-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="business-lead-detail-modal-title">
            <Building2 className="w-4 h-4" />
            Lead Details
          </DialogTitle>
          <DialogDescription data-testid="business-lead-detail-modal-description">
            {lead?.company_name || 'Lead'} • {sourceLabel}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center" data-testid="business-lead-detail-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : !lead ? (
          <div className="text-sm text-muted-foreground py-6" data-testid="business-lead-detail-empty-state">
            No lead data available.
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-5" data-testid="business-lead-detail-content">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" data-testid="business-lead-detail-source-badge">
                <Hash className="w-3 h-3 mr-1" />
                {lead.external_business_id || lead.id || 'N/A'}
              </Badge>
              <Badge variant="secondary" data-testid="business-lead-detail-lead-score-badge">
                Lead Score: {lead.lead_score ?? '-'}
              </Badge>
              <Badge
                variant={lead.verification_failed ? 'destructive' : 'default'}
                data-testid="business-lead-detail-verification-status-badge"
              >
                Verification: {lead.verification_failed ? 'Failed' : 'Passed'}
              </Badge>
            </div>

            {Array.isArray(lead.verification_reasons) && lead.verification_reasons.length > 0 && (
              <div className="rounded border border-red-200 bg-red-50 p-3" data-testid="business-lead-detail-verification-reasons">
                <p className="text-xs font-semibold text-red-700 mb-1">Verification Issues</p>
                <ul className="text-xs text-red-700 list-disc pl-4 space-y-1">
                  {lead.verification_reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PRIMARY_FIELDS.map((field) => (
                <div key={field.key} className="rounded border p-3" data-testid={`${field.testId}-wrapper`}>
                  <p className="text-xs text-muted-foreground">{field.label}</p>
                  <p className="text-sm font-medium break-all" data-testid={field.testId}>{formatValue(lead[field.key])}</p>
                </div>
              ))}
            </div>

            <Separator />

            <div className="space-y-2" data-testid="business-lead-detail-contact-quick-view">
              <h4 className="text-sm font-semibold">Quick Contact View</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-2" data-testid="business-lead-detail-quick-email"><Mail className="w-3 h-3" /> {lead.email || '-'}</div>
                <div className="flex items-center gap-2" data-testid="business-lead-detail-quick-phone"><Phone className="w-3 h-3" /> {lead.phone || '-'}</div>
                <div className="flex items-center gap-2" data-testid="business-lead-detail-quick-website"><Globe className="w-3 h-3" /> {lead.website || '-'}</div>
                <div className="flex items-center gap-2 md:col-span-3" data-testid="business-lead-detail-quick-location"><MapPin className="w-3 h-3" /> {lead.city || '-'}, {lead.state || '-'} {lead.zip_code || ''}</div>
              </div>
            </div>

            {customEntries.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2" data-testid="business-lead-detail-custom-fields-section">
                  <h4 className="text-sm font-semibold">Custom Fields</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {customEntries.map(([key, value]) => (
                      <div className="rounded border p-2" key={`custom-${key}`} data-testid={`business-lead-detail-custom-${key}`}>
                        <p className="text-xs text-muted-foreground">{key}</p>
                        <p className="text-sm break-all">{formatValue(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {dynamicEntries.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2" data-testid="business-lead-detail-additional-fields-section">
                  <h4 className="text-sm font-semibold">Additional Fields</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {dynamicEntries.map(([key, value]) => (
                      <div className="rounded border p-2" key={`dyn-${key}`} data-testid={`business-lead-detail-dynamic-${key}`}>
                        <p className="text-xs text-muted-foreground">{key}</p>
                        <p className="text-sm break-all">{formatValue(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
